<template>
  <div class="notification-container">
    <!-- Notification Bell Button with Badge -->
    <!-- Displays unread count and toggles notification panel -->
    <v-badge
      :content="unreadCount"
      :value="unreadCount"
      color="error"
      overlap
      bordered
      offset-x="16"
      offset-y="16"
    >
      <v-btn
        icon
        large
        @click="toggleNotifications"
        class="notification-btn"
        depressed
        :class="{ 'has-unread': unreadCount > 0 }"
        :loading="loading"
      >
        <!-- Dynamic icon based on unread status -->
        <v-icon :color="unreadCount > 0 ? 'primary' : 'gray'">
          {{ unreadCount > 0 ? "mdi-bell-badge" : "mdi-bell-outline" }}
        </v-icon>
      </v-btn>
    </v-badge>

    <!-- Notification Panel -->
    <!-- Slides in/out and contains all notification content -->
    <v-card
      v-if="showNotifications"
      class="notification-panel"
      elevation="2"
      outlined
    >
      <!-- Dual View Container: List View and Detail View -->
      <div
        class="view-container"
        :class="{ 'detail-active': selectedNotification }"
      >
        <!-- MAIN VIEW: Notification List -->
        <div
          class="view view-main"
          :class="{ 'slide-out': selectedNotification }"
        >
          <!-- Panel Header with Title, Stats, and Actions -->
          <v-card-title class="notification-header">
            <div class="header-content">
              <div class="header-top-row">
                <div class="header-titles">
                  <h3 class="panel-title">Notificaciones</h3>
                  <div class="panel-subtitle">
                    {{ unreadCount }} no leído de {{ notifications.length }} total
                  </div>
                </div>
                <!-- Close Panel Button -->
                <v-btn icon small @click="closePanel" class="close-btn">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>

              <div class="header-bottom-row">
                <!-- Action Buttons -->
                <div class="header-actions">
                  <!-- Mark All as Read Button -->
                  <v-btn
                    v-if="unreadCount > 0"
                    text
                    small
                    @click="markAllAsRead"
                    class="mark-all-btn"
                    :loading="markingAllAsRead"
                  >
                    Marcar todo como leído
                  </v-btn>
                </div>

                <!-- Filter Chips: All vs Unread -->
                <v-chip-group
                  v-model="filter"
                  mandatory
                  active-class="filter-active"
                  @change="onFilterChange"
                  class="filter-group"
                >
                  <v-chip small value="all" class="filter-chip" filter>
                    Todo
                  </v-chip>
                  <v-chip small value="unread" class="filter-chip" filter>
                    No leído
                  </v-chip>
                </v-chip-group>
              </div>
            </div>
          </v-card-title>

          <v-divider class="divider-light"></v-divider>

          <!-- Notification List -->
          <v-list class="notification-list" two-line>
            <!-- Notification Items Loop -->
            <template v-for="(notification, index) in filteredNotifications">
              <v-list-item
                :key="notification.id"
                @click="openNotificationDetail(notification)"
                class="notification-item"
                :class="{
                  unread: !notification.read,
                }"
              >
                <!-- Notification Avatar with Type and Read Status -->
                <v-list-item-avatar class="notification-avatar">
                  <div
                    class="avatar-container"
                    :class="`type-${getNotificationType(notification.type)}`"
                  >
                    <!-- Sender Avatar or Fallback Initials -->
                    <v-img
                      v-if="notification.sender && notification.sender.avatar"
                      :src="notification.sender.avatar"
                      class="avatar-image"
                    ></v-img>
                    <div v-else class="avatar-fallback">
                      {{ getInitials(getSenderName(notification)) }}
                    </div>
                    <!-- Unread Indicator Dot -->
                    <div v-if="!notification.read" class="unread-dot"></div>
                  </div>
                </v-list-item-avatar>

                <!-- Notification Content -->
                <v-list-item-content class="notification-content">
                  <div class="notification-main">
                    <div class="notification-title">
                      {{ notification.title }}
                    </div>
                    <div class="notification-message">
                      {{ notification.message }}
                    </div>
                  </div>

                  <!-- Notification Metadata -->
                  <div class="notification-meta">
                    <div class="meta-left">
                      <span class="sender-name">
                        {{ getSenderName(notification) }}
                      </span>
                      <span class="sender-role">
                        {{ getSenderRole(notification) }}
                      </span>
                    </div>
                    <div class="meta-right">
                      <!-- Timestamp -->
                      <div class="notification-time">
                        {{ formatTime(notification.timestamp) }}
                      </div>
                      <!-- Priority Badge -->
                      <v-chip
                        x-small
                        :color="getPriorityColor(notification.priority)"
                        text-color="white"
                        class="priority-chip"
                      >
                        {{ getPriorityText(notification.priority) }}
                      </v-chip>
                    </div>
                  </div>
                </v-list-item-content>
              </v-list-item>

              <!-- Divider between notifications -->
              <v-divider
                v-if="index < filteredNotifications.length - 1"
                :key="`divider-${notification.id}`"
                class="divider-light"
              ></v-divider>
            </template>

            <!-- Empty State -->
            <div
              v-if="filteredNotifications.length === 0 && !loading"
              class="empty-state"
            >
              <v-icon size="48" color="gray lighten-2" class="empty-icon">
                mdi-bell-off-outline
              </v-icon>
              <div class="empty-title">Sin notificaciones</div>
              <div class="empty-subtitle">
                {{
                  filter === "unread"
                    ? "Todas las notificaciones estan leídas"
                    : "No hay notificaciones para mostrar"
                }}
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="loading-state">
              <v-progress-circular
                indeterminate
                color="primary"
                size="32"
              ></v-progress-circular>
              <div class="loading-text">Cargando notificaciones...</div>
            </div>

            <!-- Load More Button -->
            <div v-if="hasMore && !loading" class="load-more-container">
              <v-btn
                text
                small
                @click="loadMore"
                class="load-more-btn"
                :loading="loadingMore"
              >
                Cargar más
              </v-btn>
            </div>
          </v-list>
        </div>

        <!-- DETAIL VIEW: Individual Notification Detail -->
        <div
          class="view view-detail"
          :class="{ 'slide-in': selectedNotification }"
        >
          <div class="detail-view-container">
            <!-- Detail Header with Back Navigation -->
            <v-card-title class="detail-header">
              <div class="detail-header-content">
                <v-btn icon small @click="closeDetail" class="back-btn">
                  <v-icon small>mdi-arrow-left</v-icon>
                </v-btn>
                <div class="detail-header-title">Detalle de notificación</div>
                <v-btn icon small @click="closePanel" class="close-btn">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
            </v-card-title>

            <v-divider class="divider-light"></v-divider>

            <!-- Detail Content -->
            <v-card-text class="detail-content" v-if="selectedNotification">
              <div class="detail-main">
                <!-- Notification Header Information -->
                <div class="detail-info">
                  <div class="detail-title-section">
                    <h4 class="detail-title">
                      {{ selectedNotification.title }}
                    </h4>
                    <!-- Sender Information -->
                    <div class="detail-sender">
                      <div
                        class="sender-avatar"
                        :class="`type-${getNotificationType(
                          selectedNotification.type
                        )}`"
                      >
                        <v-img
                          v-if="
                            selectedNotification.sender &&
                            selectedNotification.sender.avatar
                          "
                          :src="selectedNotification.sender.avatar"
                        ></v-img>
                        <div v-else class="avatar-fallback">
                          {{ getInitials(getSenderName(selectedNotification)) }}
                        </div>
                      </div>
                      <div class="sender-info">
                        <div class="sender-name">
                          {{ getSenderName(selectedNotification) }}
                        </div>
                        <div class="sender-role">
                          {{ getSenderRole(selectedNotification) }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Timestamp and Priority -->
                  <div class="detail-time">
                    <div class="date">
                      {{ formatDate(selectedNotification.timestamp) }}
                    </div>
                    <div class="time">
                      {{ formatTime(selectedNotification.timestamp) }}
                    </div>
                    <v-chip
                      small
                      :color="getPriorityColor(selectedNotification.priority)"
                      text-color="white"
                      class="detail-priority-chip"
                    >
                      Prioridad:
                      {{ getPriorityText(selectedNotification.priority) }}
                    </v-chip>
                  </div>
                </div>

                <!-- Main Message Card -->
                <div class="detail-message-card">
                  <div class="message-label">Mensaje</div>
                  <div class="detail-message">
                    {{ selectedNotification.message }}
                  </div>
                </div>

                <!-- Metadata Section: Type-specific additional data -->
                <div
                  v-if="selectedNotification.metadata"
                  class="metadata-section"
                >
                  <div class="section-label">Información adicional</div>
                  <div class="metadata-grid">
                    <div
                      v-for="(value, key) in getMetadataForDisplay(
                        selectedNotification
                      )"
                      :key="key"
                      class="metadata-item"
                    >
                      <div class="metadata-label">
                        {{ formatMetadataKey(key) }}
                      </div>
                      <div class="metadata-value">
                        {{ formatMetadataValue(value) }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Related Entities Section -->
                <div
                  v-if="hasRelatedEntities(selectedNotification)"
                  class="related-entities-section"
                >
                  <div class="section-label">Entidades relacionadas</div>
                  <div class="related-entities-grid">
                    <!-- Molecular Marker Entity -->
                    <div
                      v-if="
                        selectedNotification.relatedEntities &&
                        selectedNotification.relatedEntities.molecularMarker
                      "
                      class="entity-item"
                    >
                      <div class="entity-label">Marcador molecular</div>
                      <div class="entity-value">
                        {{
                          selectedNotification.relatedEntities.molecularMarker
                            .molecularMarkerID
                        }}
                      </div>
                    </div>
                    <!-- Package Entity -->
                    <div
                      v-if="
                        selectedNotification.relatedEntities &&
                        selectedNotification.relatedEntities.package
                      "
                      class="entity-item"
                    >
                      <div class="entity-label">Paquete</div>
                      <div class="entity-value">
                        {{ selectedNotification.relatedEntities.package.name }}
                      </div>
                    </div>
                    <!-- Laboratory Entity -->
                    <div
                      v-if="
                        selectedNotification.relatedEntities &&
                        selectedNotification.relatedEntities.laboratory
                      "
                      class="entity-item"
                    >
                      <div class="entity-label">Laboratorio</div>
                      <div class="entity-value">
                        {{
                          selectedNotification.relatedEntities.laboratory.name
                        }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Action Button (Commented out for future implementation) -->
                <!-- <div v-if="selectedNotification.action" class="action-section">
                  <v-btn
                    color="primary"
                    @click="handleAction(selectedNotification)"
                    class="primary-action"
                    depressed
                    block
                  >
                    {{ getActionText(selectedNotification.action.type) }}
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </div> -->
              </div>
            </v-card-text>
          </div>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  name: "NotificationCenter",

  data() {
    return {
      showNotifications: false, // Controls panel visibility
      selectedNotification: null, // Currently selected notification for detail view
      isAnimating: false, // Prevents interactions during animations
    };
  },

  computed: {
    /**
     * Maps Vuex state to component computed properties
     * Connects to notification module state
     */
    ...mapState("notifications", [
      "unreadCount", // Total count of unread notifications
      "loading", // Main loading state for initial load
      "loadingMore", // Loading state for pagination
      "markingAllAsRead", // Loading state for bulk read operation
      "hasMore", // Whether more notifications are available
      "currentPage", // Current pagination page
    ]),

    /**
     * Maps Vuex getters to component computed properties
     * Provides filtered and processed notification data
     */
    ...mapGetters("notifications", [
      "notifications", // All notifications array
      "filteredNotifications", // Notifications filtered by current filter
    ]),

    /**
     * Two-way computed property for filter
     * Syncs local filter state with Vuex store
     */
    filter: {
      get() {
        return this.$store.state.notifications.filter;
      },
      set(value) {
        this.changeFilter(value);
      },
    },
  },

  methods: {
    /**
     * Maps Vuex actions to component methods
     * Handles all notification-related operations
     */
    ...mapActions("notifications", [
      "loadNotifications", // Load initial notifications
      "loadMoreNotifications", // Load additional pages
      "markAsRead", // Mark single notification as read
      "markAllAsRead", // Mark all notifications as read
      "fetchUnreadCount", // Refresh unread count
      "changeFilter", // Change current filter
    ]),

    /**
     * Toggles notification panel visibility
     * Loads notifications when opening panel
     */
    async toggleNotifications() {
      this.showNotifications = !this.showNotifications;
      if (this.showNotifications) {
        await this.loadNotifications({ page: 1 });
      } else {
        this.selectedNotification = null;
      }
    },

    /**
     * Closes the entire notification panel
     * Resets detail view state
     */
    closePanel() {
      this.showNotifications = false;
      this.selectedNotification = null;
    },

    /**
     * Loads more notifications for pagination
     */
    async loadMore() {
      await this.loadMoreNotifications();
    },

    /**
     * Opens notification detail view
     * Marks notification as read if unread
     * @param {Object} notification - Notification to display in detail
     */
    async openNotificationDetail(notification) {
      if (this.isAnimating) return;

      this.isAnimating = true;

      // Mark as read if notification is unread
      if (!notification.read) {
        await this.markAsRead(notification.id);
      }

      this.selectedNotification = notification;

      setTimeout(() => {
        this.isAnimating = false;
      }, 400);
    },

    /**
     * Handles filter change events
     * Reloads notifications with new filter
     */
    async onFilterChange() {
      await this.changeFilter(this.filter);
    },

    /**
     * Closes detail view and returns to list view
     */
    closeDetail() {
      if (this.isAnimating) return;

      this.isAnimating = true;
      this.selectedNotification = null;

      setTimeout(() => {
        this.isAnimating = false;
      }, 400);
    },

    /**
     * Extracts sender name from notification
     * @param {Object} notification - Notification object
     * @returns {string} Sender name or "Sistema" as fallback
     */
    getSenderName(notification) {
      return (notification.sender && notification.sender.name) || "Sistema";
    },

    /**
     * Extracts sender role from notification
     * @param {Object} notification - Notification object
     * @returns {string} Sender role or "Sistema" as fallback
     */
    getSenderRole(notification) {
      return (notification.sender && notification.sender.role) || "Sistema";
    },

    /**
     * Checks if notification has related entities
     * @param {Object} notification - Notification object
     * @returns {boolean} True if related entities exist
     */
    hasRelatedEntities(notification) {
      return (
        notification.relatedEntities &&
        (notification.relatedEntities.molecularMarker ||
          notification.relatedEntities.package ||
          notification.relatedEntities.laboratory)
      );
    },

    /**
     * Maps backend notification types to frontend display types
     * @param {string} backendType - Backend notification type
     * @returns {string} Frontend display type
     */
    getNotificationType(backendType) {
      const typeMap = {
        CQ_MODIFICATION: "message",
        PACKAGE_ACTIVATION: "success",
        PACKAGE_DEACTIVATION: "alert",
        PACKAGE_MODIFICATION: "task",
        PACKAGE_TRANSFER: "system",
        MM_INTEGRATION: "success",
        MM_LIBERATION: "success",
      };
      return typeMap[backendType] || "system";
    },

    /**
     * Maps priority levels to Vuetify color names
     * @param {string} priority - Priority level
     * @returns {string} Vuetify color name
     */
    getPriorityColor(priority) {
      const colors = {
        high: "error",
        medium: "warning",
        low: "success",
      };
      return colors[priority] || "gray";
    },

    /**
     * Maps priority levels to display text
     * @param {string} priority - Priority level
     * @returns {string} Display text for priority
     */
    getPriorityText(priority) {
      const texts = {
        high: "Alta",
        medium: "Media",
        low: "Baja",
      };
      return texts[priority] || "Normal";
    },

    /**
     * Generates initials from sender name for avatar fallback
     * @param {string} name - Sender name
     * @returns {string} Uppercase initials (max 2 characters)
     */
    getInitials(name) {
      if (!name) return "S";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    },

    /**
     * Formats timestamp to time string
     * @param {string} timestamp - ISO timestamp
     * @returns {string} Formatted time (HH:MM)
     */
    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleTimeString("es-MX", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    /**
     * Formats timestamp to date string
     * @param {string} timestamp - ISO timestamp
     * @returns {string} Formatted date
     */
    formatDate(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleDateString("es-MX", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    /**
     * Formats metadata keys for display
     * Converts snake_case to Title Case
     * @param {string} key - Metadata key
     * @returns {string} Formatted display key
     */
    formatMetadataKey(key) {
      return key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },

    /**
     * Formats metadata values for display
     * Handles objects by converting to JSON string
     * @param {any} value - Metadata value
     * @returns {string} Formatted display value
     */
    formatMetadataValue(value) {
      if (typeof value === "object") {
        return JSON.stringify(value, null, 2);
      }
      return value;
    },

    /**
     * Extracts and formats metadata for display based on notification type
     * @param {Object} notification - Notification object
     * @returns {Object} Formatted metadata for display
     */
    getMetadataForDisplay(notification) {
      const metadata = {};

      if (notification.metadata) {
        switch (notification.type) {
          case "CQ_MODIFICATION":
          case "CQ_RANGE_UPDATE":
            if (notification.metadata.cqChanges) {
              metadata["Valores Anteriores"] = `CQ Min: ${
                notification.metadata.cqChanges.oldValues &&
                notification.metadata.cqChanges.oldValues.cqMin
              }, CQ Max: ${
                notification.metadata.cqChanges.oldValues &&
                notification.metadata.cqChanges.oldValues.cqMax
              }`;
              metadata["Valores Nuevos"] = `CQ Min: ${
                notification.metadata.cqChanges.newValues &&
                notification.metadata.cqChanges.newValues.cqMin
              }, CQ Max: ${
                notification.metadata.cqChanges.newValues &&
                notification.metadata.cqChanges.newValues.cqMax
              }`;
            }
            break;

          case "PACKAGE_ACTIVATION":
          case "PACKAGE_DEACTIVATION":
            if (notification.metadata.packageStatus) {
              metadata["Estado Anterior"] = notification.metadata.packageStatus
                .previous
                ? "Activo"
                : "Inactivo";
              metadata["Estado Actual"] = notification.metadata.packageStatus
                .current
                ? "Activo"
                : "Inactivo";
            }
            break;

          case "PACKAGE_TRANSFER":
            if (notification.metadata.packageTransfer) {
              metadata["Laboratorio Origen"] =
                (notification.metadata.packageTransfer.fromLaboratory &&
                  notification.metadata.packageTransfer.fromLaboratory.name) ||
                "N/A";
              metadata["Laboratorio Destino"] =
                (notification.metadata.packageTransfer.toLaboratory &&
                  notification.metadata.packageTransfer.toLaboratory.name) ||
                "N/A";
            }
            break;

          case "MM_INTEGRATION":
          case "MM_LIBERATION":
            if (notification.metadata.mmIntegration) {
              metadata["ID Marcador"] =
                notification.metadata.mmIntegration.molecularMarkerID;
              metadata["Versión"] = notification.metadata.mmIntegration.version;
              metadata["Estado"] = notification.metadata.mmIntegration.status;
            }
            break;
        }
      }

      return metadata;
    },

    /**
     * Maps action types to display text
     * @param {string} actionType - Action type
     * @returns {string} Display text for action
     */
    getActionText(actionType) {
      const texts = {
        navigate: "Abrir",
        view: "Ver detalles",
        download: "Descargar",
        confirm: "Confirmar",
      };
      return texts[actionType] || "Acción";
    },

    /**
     * Handles notification action execution
     * Navigates to related entities or performs actions
     * @param {Object} notification - Notification with action
     */
    handleAction(notification) {
      if (notification.action) {
        switch (notification.action.type) {
          case "navigate":
            this.$router.push(notification.action.target);
            break;
          case "view":
            if (
              notification.relatedEntities &&
              notification.relatedEntities.molecularMarker
            ) {
              const markerId =
                notification.relatedEntities.molecularMarker._id ||
                notification.relatedEntities.molecularMarker;
              this.$router.push(`/molecular-markers/${markerId}`);
            } else if (
              notification.relatedEntities &&
              notification.relatedEntities.package
            ) {
              const packageId =
                notification.relatedEntities.package._id ||
                notification.relatedEntities.package;
              this.$router.push(`/packages/${packageId}`);
            }
            break;
        }
      }
      this.closePanel();
    },
  },

  watch: {
    /**
     * Watches panel visibility to handle click outside events
     */
    showNotifications(newVal) {
      if (newVal) {
        document.addEventListener("click", this.handleClickOutside);
      } else {
        document.removeEventListener("click", this.handleClickOutside);
        this.selectedNotification = null;
      }
    },
  },

  beforeDestroy() {
    /**
     * Cleanup event listeners when component is destroyed
     */
    document.removeEventListener("click", this.handleClickOutside);
  },

  created() {
    /**
     * Initialize click outside handler and load initial unread count
     */
    this.handleClickOutside = (event) => {
      if (!this.$el.contains(event.target) && !this.isAnimating) {
        this.showNotifications = false;
      }
    };

    // Load initial unread count when component is created
    this.fetchUnreadCount();
  },
};
</script>

<style scoped>
/* Main container for notification component */
.notification-container {
  position: relative;
  display: inline-block;
}

/* Notification bell button styles */
.notification-btn {
  background: white !important;
  border: 1px solid #e0e0e0 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.notification-btn:hover {
  border-color: #04c105 !important;
  transform: translateY(-1px);
}

.notification-btn.has-unread {
  border-color: #04c105 !important;
}

/* Main notification panel container */
.notification-panel {
  position: absolute;
  top: 100%;
  right: 0;
  width: 420px;
  max-width: calc(100vw - 20px);
  z-index: 1000;
  margin-top: 8px;
  border-radius: 12px;
  background: white;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

/* Dual-view container for list and detail views */
.view-container {
  position: relative;
  width: 100%;
  height: 500px;
  overflow: hidden;
}

/* Base view styles for both list and detail views */
.view {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
}

/* Main list view animations */
.view-main {
  transform: translateX(0);
}

.view-main.slide-out {
  transform: translateX(-100%);
}

/* Detail view animations */
.view-detail {
  transform: translateX(100%);
}

.view-detail.slide-in {
  transform: translateX(0);
}

/* Panel header styling */
.notification-header {
  padding: 20px 20px 16px 20px;
  background: white;
}

.header-content {
  width: 100%;
}

/* Header top row with title and close button */
.header-top-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 12px;
}

.header-titles {
  flex: 1;
  min-width: 0;
}

.panel-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.2;
}

.panel-subtitle {
  font-size: 0.875rem;
  color: #616161;
  margin-top: 4px;
  line-height: 1.3;
}

/* Header bottom row with actions and filters */
.header-bottom-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  gap: 8px;
}

.header-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.mark-all-btn {
  color: #04c105 !important;
  font-size: 0.75rem;
  text-transform: none;
  white-space: nowrap;
}

.close-btn {
  color: #616161;
  flex-shrink: 0;
}

/* Filter chip group styling */
.filter-group {
  flex: 1;
  justify-content: flex-end;
  min-width: 0;
}

.filter-chip {
  background: #f5f5f5 !important;
  color: #616161 !important;
  border: none;
  font-size: 0.75rem;
}

.filter-active {
  background: #04c105 !important;
  color: white !important;
}

/* Notification list container */
.notification-list {
  padding: 0;
  height: calc(100% - 160px);
  overflow-y: auto;
}

/* Individual notification item */
.notification-item {
  padding: 16px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  min-height: 80px;
}

.notification-item:hover {
  background: #f8f9fa;
}

.notification-item.unread {
  background: #f8fff8;
  border-left-color: #04c105;
}

/* Avatar container for sender images */
.avatar-container {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  flex-shrink: 0;
}

.avatar-image {
  border-radius: 10px;
  width: 100%;
  height: 100%;
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #616161;
}

/* Notification type color coding */
.type-message {
  background: #05a1e9;
}
.type-task {
  background: #027223;
}
.type-reminder {
  background: #ff8f1c;
}
.type-system {
  background: #1c4220;
}
.type-alert {
  background: #cb3234;
}
.type-success {
  background: #05c105;
}

/* Unread notification indicator dot */
.unread-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #04c105;
  border: 2px solid white;
}

/* Notification content area */
.notification-content {
  padding: 0;
  min-width: 0;
}

.notification-main {
  margin-bottom: 8px;
}

.notification-title {
  font-weight: 600;
  color: #1a1a1a;
  font-size: 0.9rem;
  margin-bottom: 4px;
  line-height: 1.3;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.notification-message {
  color: #616161;
  font-size: 0.8rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-wrap: break-word;
}

/* Notification metadata (sender, time, priority) */
.notification-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  flex-wrap: wrap;
  gap: 4px;
}

.meta-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.sender-name {
  font-size: 0.75rem;
  color: #1c4220;
  font-weight: 500;
  white-space: nowrap;
}

.sender-role {
  font-size: 0.7rem;
  color: #616161;
  white-space: nowrap;
}

.meta-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.notification-time {
  font-size: 0.7rem;
  color: #616161;
  white-space: nowrap;
}

.priority-chip {
  height: 16px;
  font-size: 0.6rem;
  font-weight: 600;
  white-space: nowrap;
}

/* Empty state when no notifications */
.empty-state {
  padding: 48px 20px;
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.empty-icon {
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-title {
  font-size: 1rem;
  color: #616161;
  margin-bottom: 8px;
  text-align: center;
}

.empty-subtitle {
  font-size: 0.875rem;
  color: #9e9e9e;
  text-align: center;
  max-width: 300px;
}

/* Detail view container */
.detail-view-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.detail-header {
  padding: 16px 20px;
  background: white;
  flex-shrink: 0;
}

.detail-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
}

.detail-header-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a1a;
  text-align: center;
  flex: 1;
}

.back-btn {
  color: #616161;
  flex-shrink: 0;
}

.detail-content {
  padding: 0;
  flex: 1;
  overflow-y: auto;
}

.detail-main {
  padding: 20px;
}

/* Detail view information layout */
.detail-info {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
}

.detail-title-section {
  flex: 1;
  min-width: 0;
}

.detail-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 16px 0;
  line-height: 1.3;
  word-wrap: break-word;
}

.detail-sender {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sender-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: white;
  flex-shrink: 0;
}

.sender-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.sender-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #1c4220;
  line-height: 1.2;
}

.sender-role {
  font-size: 0.8rem;
  color: #616161;
  line-height: 1.2;
}

.detail-time {
  text-align: right;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
}

.date {
  font-size: 0.9rem;
  color: #1a1a1a;
  font-weight: 500;
  white-space: nowrap;
}

.time {
  font-size: 0.8rem;
  color: #616161;
  white-space: nowrap;
}

.detail-priority-chip {
  margin-top: 8px;
  white-space: nowrap;
}

/* Main message card in detail view */
.detail-message-card {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 24px;
  border-left: 4px solid #04c105;
}

.message-label {
  font-size: 0.8rem;
  color: #616161;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.detail-message {
  color: #1a1a1a;
  font-size: 0.95rem;
  line-height: 1.5;
  word-wrap: break-word;
}

/* Metadata section styling */
.metadata-section {
  margin-bottom: 24px;
}

.section-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 12px;
}

.metadata-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.metadata-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  min-width: 0;
}

.metadata-label {
  font-size: 0.75rem;
  color: #616161;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.2;
}

.metadata-value {
  font-size: 0.875rem;
  color: #1a1a1a;
  font-weight: 500;
  word-wrap: break-word;
  line-height: 1.3;
}

/* Action button section */
.action-section {
  margin-top: 24px;
}

.primary-action {
  background: linear-gradient(135deg, #04c105 0%, #027223 100%) !important;
  color: white !important;
  border-radius: 8px;
  padding: 12px 24px;
  text-transform: none;
  font-weight: 500;
  font-size: 0.9rem;
  min-height: 48px;
}

/* Utility classes */
.divider-light {
  border-color: #f0f0f0;
}

/* Custom scrollbar styling */
.notification-list::-webkit-scrollbar {
  width: 4px;
}

.notification-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.notification-list::-webkit-scrollbar-thumb {
  background: #04c105;
  border-radius: 2px;
}

.notification-list::-webkit-scrollbar-thumb:hover {
  background: #027223;
}

.detail-content::-webkit-scrollbar {
  width: 4px;
}

.detail-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.detail-content::-webkit-scrollbar-thumb {
  background: #05a1e9;
  border-radius: 2px;
}

/* Loading state styling */
.loading-state {
  padding: 40px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-text {
  color: #616161;
  font-size: 0.9rem;
}

.load-more-container {
  padding: 16px;
  text-align: center;
  border-top: 1px solid #f0f0f0;
}

.load-more-btn {
  color: #04c105 !important;
  text-transform: none;
}

/* Related entities section */
.related-entities-section {
  margin-bottom: 24px;
}

.related-entities-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.entity-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #05a1e9;
}

.entity-label {
  font-size: 0.75rem;
  color: #616161;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.entity-value {
  font-size: 0.875rem;
  color: #1a1a1a;
  font-weight: 500;
}

/* Responsive design for tablets and smaller screens */
@media (max-width: 600px) {
  .notification-panel {
    width: calc(100vw - 20px);
    right: -10px;
    max-width: none;
  }

  .notification-header {
    padding: 16px;
  }

  .header-top-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .header-bottom-row {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .header-actions {
    justify-content: center;
    width: 100%;
  }

  .filter-group {
    justify-content: center;
    width: 100%;
  }

  .notification-item {
    padding: 12px 16px;
  }

  .notification-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .meta-right {
    width: 100%;
    justify-content: space-between;
  }

  .detail-main {
    padding: 16px;
  }

  .detail-info {
    flex-direction: column;
    align-items: stretch;
  }

  .detail-time {
    text-align: left;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .metadata-grid {
    grid-template-columns: 1fr;
  }

  .detail-header-content {
    gap: 8px;
  }

  .detail-header-title {
    font-size: 1rem;
  }

  .view-container {
    height: 400px;
  }
}

/* Mobile-specific responsive adjustments */
@media (max-width: 480px) {
  .notification-panel {
    width: 100vw;
    right: 0;
    border-radius: 0;
    margin-top: 0;
  }

  .view-container {
    height: 70vh;
    max-height: 500px;
  }

  .detail-title {
    font-size: 1.2rem;
  }

  .detail-message-card {
    padding: 16px;
  }

  .metadata-item {
    padding: 10px;
  }

  .notification-list {
    height: calc(100% - 140px);
  }
}

/* Small mobile device optimizations */
@media (max-width: 360px) {
  .header-titles {
    min-width: 0;
  }

  .panel-title {
    font-size: 1.1rem;
  }

  .panel-subtitle {
    font-size: 0.8rem;
  }

  .notification-title {
    font-size: 0.85rem;
  }

  .notification-message {
    font-size: 0.75rem;
  }

  .detail-title {
    font-size: 1.1rem;
  }

  .detail-message {
    font-size: 0.9rem;
  }
}

/* Accessibility improvements */
.notification-btn:focus {
  outline: 2px solid #04c105;
  outline-offset: 2px;
}

.notification-item:focus {
  outline: 2px solid #05a1e9;
  outline-offset: -2px;
}

.primary-action:focus {
  outline: 2px solid white;
  outline-offset: 2px;
}

/* Performance optimizations */
.view {
  will-change: transform;
  backface-visibility: hidden;
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
  .notification-item:hover {
    background: transparent;
  }

  .notification-item.unread:hover {
    background: #f8fff8;
  }

  .notification-btn {
    min-width: 48px;
    min-height: 48px;
  }
}
</style>
